import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { getAircraftStatusReport, getComplaints, getComplaint, getComplaintActions, complaintActionsEnabled, updateComplaint } from '../services/api';
import { useAuth } from '../context/AuthContext';
import openComplaintsIcon from '../assets/icons/opencomplaints.svg';
import mccIcon from '../assets/icons/mcc.svg';
import cabinItemsIcon from '../assets/icons/cabinitems.svg';
import arrowLeftIcon from '../assets/icons/arrow-sm-left.svg';
import './AircraftStatusReport.css';

const FILTER_OPTIONS = [
  { id: 'work_order_number', label: 'Workorder / Complaint Number' },
  { id: 'section', label: 'Last Deferral Type' },
  { id: 'status', label: 'Status' },
  { id: 'mcc_reference', label: 'Temporary Workorder Number' },
  { id: 'ata_chapter', label: 'ATA Chapter' },
  { id: 'type', label: 'Type of Workorder' }
];

const CALENDAR_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const VOID_ACTION_TEXT = 'Void- workorder closed due to mistake';
const ADMIN_ROLES = new Set(['admin', 'administrator', 'super_admin', 'superadmin']);
const isAdminRole = role => {
  const value = String(role || '').trim().toLowerCase();
  return ADMIN_ROLES.has(value) || value.includes('admin');
};
const isComplaintLocked = complaint => {
  const value = complaint?.is_locked;
  return value === true || value === 1 || String(value).trim().toLowerCase() === 'true' || String(value).trim() === '1';
};
const Chevron = () => <span className="asr-chevron" aria-hidden="true">›</span>;

const getStatusClass = (status) => `status-${String(status || 'unknown').toLowerCase()}`;
const isTerminalComplaintStatus = (status) => ['CLOSED', 'VOID', 'VOIDED'].includes(String(status || '').toUpperCase());

const formatDisplayDate = (value) => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '---' : format(date, 'dd.MMM.yyyy');
};

const formatDisplayDateTime = (value) => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '---' : format(date, 'dd.MMM.yyyy HH:mm');
};

const parseActionMetadata = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const extractMelReference = (...values) => {
  const combined = values.filter(Boolean).join(' ');
  const matches = combined.match(/\b\d{2}-\d{2}-\d{2}[A-Z]?\b/g);
  return matches?.[0] || '';
};

const formatPreciseToGo = (dueAt) => {
  if (!dueAt) return '';
  const dueDate = new Date(dueAt);
  if (Number.isNaN(dueDate.getTime())) return '';
  const totalHours = Math.max(0, Math.ceil((dueDate.getTime() - Date.now()) / 3600000));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days && hours) return `${days}d ${hours}h`;
  if (days) return `${days}d 0h`;
  return `${hours}h`;
};

const formatRemainingToGoDays = (dueAt, now = Date.now()) => {
  if (!dueAt) return '';
  const dueDate = new Date(dueAt);
  if (Number.isNaN(dueDate.getTime())) return '';
  const days = Math.max(0, Math.ceil((dueDate.getTime() - now) / 86400000));
  return `${days} ${days === 1 ? 'Day' : 'Days'}`;
};

const formatDeferralType = (metadata, complaint) => {
  const category = String(complaint?.deferral_category || metadata.category || '').toUpperCase();
  const itemCategory = String(complaint?.deferral_type || metadata.deferral_type || '').toUpperCase();
  if (!category && !itemCategory) return '';
  if (category === 'DD') return itemCategory ? `DD ${itemCategory}` : 'DD';
  return itemCategory ? `MEL ${itemCategory}` : 'MEL';
};

const getCachedDeferralData = (complaintId) => {
  if (!complaintId) return {};
  try {
    const cache = JSON.parse(localStorage.getItem('etlb_complaint_deferrals') || '{}');
    return cache[String(complaintId)] || {};
  } catch {
    return {};
  }
};

const getWorkorderStatusUpdateText = (action) => {
  const actionType = String(action?.action_type || '').toUpperCase();
  const statusFrom = String(action?.status_from || '').toUpperCase();
  const statusTo = String(action?.status_to || '').toUpperCase();

  if (actionType === 'CREATED' || (!statusFrom && statusTo === 'OPEN')) return 'WORKORDER OPENED.';
  if (statusTo === 'DEFERRED' && statusFrom !== 'DEFERRED') return 'WORKORDER DEFERRED.';
  if (statusTo === 'CLOSED' && statusFrom !== 'CLOSED') return 'WORKORDER CLOSED.';
  if (actionType === 'STATUS' && action?.action_text) return action.action_text;
  return '';
};

const shouldRenderWorkorderActionCard = (action) => {
  const actionType = String(action?.action_type || '').toUpperCase();
  if (['CREATED', 'STATUS', 'CLOSE'].includes(actionType)) return false;
  return Boolean(action?.action_text || action?.metadata_json || actionType === 'VOID' || actionType === 'DISPATCH' || actionType === 'ACTION');
};

const uniqueComplaints = (...lists) => {
  const itemsByKey = new Map();
  lists.flat().filter(Boolean).forEach(item => {
    const key = item.id || item.work_order_number || `${item.defect_description}-${item.opened_at}`;
    itemsByKey.set(String(key), { ...(itemsByKey.get(String(key)) || {}), ...item });
  });
  return [...itemsByKey.values()];
};

const sortComplaintsNewestFirst = (items) => (
  [...items].sort((a, b) => new Date(b.opened_at || b.updated_at || 0) - new Date(a.opened_at || a.updated_at || 0))
);

const getComplaintStatus = (complaint) => String(complaint?.status || '').toUpperCase();
const getComplaintSection = (complaint) => String(complaint?.section || '').toLowerCase();
const getComplaintDeferralCategory = (complaint) => String(complaint?.deferral_category || '').toUpperCase();

const isDdHilComplaint = (complaint) => {
  if (getComplaintStatus(complaint) !== 'DEFERRED') return false;
  return getComplaintDeferralCategory(complaint) === 'DD' || getComplaintSection(complaint) === 'non_airworthy_deferred';
};

const isMelComplaint = (complaint) => {
  if (getComplaintStatus(complaint) !== 'DEFERRED' || isDdHilComplaint(complaint)) return false;
  const category = getComplaintDeferralCategory(complaint);
  if (category === 'CDL') return false;
  const section = getComplaintSection(complaint);
  return category === 'MEL'
    || ['airworthy_deferred', 'mcc_approved'].includes(section)
    || Boolean(complaint?.mel_cdl_ref);
};

const SectionTitle = ({ children }) => (
  <h3 className="briefing-section-title">{children}</h3>
);

const EmptyStrip = ({ label }) => (
  <div className="briefing-empty-strip">{label || 'no items'}</div>
);

const CompactInfoRow = ({ icon, label, rightText = 'no items', iconClassName = '' }) => (
  <div className="briefing-compact-row">
    <div className="briefing-compact-left">
      <img src={icon} alt="" className={`asr-inline-icon app-icon ${iconClassName}`.trim()} />
      <span>{label}</span>
    </div>
    <div className="briefing-compact-right">{rightText}</div>
  </div>
);

const NextDueEventsCard = ({ items, onOpenItem }) => (
  <div className="briefing-block-card">
    <div className="briefing-block-header">Open Items</div>
    {items?.length ? (
      <div className="briefing-open-items-list">
        {items.map(item => (
          <button key={item.id} type="button" className={`briefing-open-item ${getComplaintLineClass(item)}`} onClick={() => onOpenItem?.(item)}>
            <div className="briefing-open-item-main">
              <div className="briefing-open-item-top">
                <span className="briefing-open-item-wo">{item.work_order_number || `WO.${item.id}`}</span>
                <span className={`briefing-complaint-badge ${getStatusBadgeClass(item)}`}>{getStatusBadgeLabel(item, 'OPEN')}</span>
                <span className="briefing-complaint-tag">{item.type || item.category || 'Pilot'}</span>
              </div>
              <div className="briefing-open-item-desc">{item.defect_description}</div>
            </div>
            <Chevron />
          </button>
        ))}
      </div>
    ) : (
      <EmptyStrip />
    )}
  </div>
);

const DowngradeCard = ({ item }) => (
  <div className="briefing-block-card briefing-block-card-tight" key={item.id}>
    <div className="briefing-card-headline">
      <img src={mccIcon} alt="" className="asr-inline-icon app-icon app-icon-mcc" />
      <span>Aircraft Downgrades/Limitations</span>
    </div>
    <div className="briefing-downgrade-box">
      <div className="briefing-downgrade-pair">
        <span className="briefing-mini-label">Type.</span>
        <span className="briefing-mini-value">{item.type || item.category || item.section || '---'}</span>
      </div>
      <div className="briefing-downgrade-pair">
        <span className="briefing-mini-label">Workorder IDs.</span>
        <span className="briefing-mini-value">{item.work_order_number || `WO.${item.id}`}</span>
      </div>
    </div>
  </div>
);

const isComplaintOverdue = (complaint) => {
  if (!complaint?.due_at) return false;
  const dueDate = new Date(complaint.due_at);
  return !Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now();
};

const shouldShowOverdueStatus = (complaint) => {
  const status = String(complaint?.status || '').toUpperCase();
  return !isTerminalComplaintStatus(status) && isComplaintOverdue(complaint);
};

const getStatusBadgeLabel = (complaint, fallback = '---') => (
  shouldShowOverdueStatus(complaint) ? 'OVERDUE' : (complaint?.status || fallback)
);

const getStatusBadgeClass = (complaint) => (
  shouldShowOverdueStatus(complaint) ? 'status-overdue' : getStatusClass(complaint?.status)
);

const isNextDueMaintenanceItem = (complaint) => (
  getComplaintStatus(complaint) === 'OPEN' || shouldShowOverdueStatus(complaint)
);

const getDueTime = (complaint) => {
  if (!complaint?.due_at) return Number.POSITIVE_INFINITY;
  const dueDate = new Date(complaint.due_at);
  return Number.isNaN(dueDate.getTime()) ? Number.POSITIVE_INFINITY : dueDate.getTime();
};

const sortNextDueMaintenanceItems = (items) => (
  [...items].sort((a, b) => {
    const aOverdue = shouldShowOverdueStatus(a);
    const bOverdue = shouldShowOverdueStatus(b);
    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

    const aDue = getDueTime(a);
    const bDue = getDueTime(b);
    if (aDue !== bDue) {
      if (!Number.isFinite(aDue)) return 1;
      if (!Number.isFinite(bDue)) return -1;
      return aDue - bDue;
    }

    return new Date(b.opened_at || b.updated_at || 0) - new Date(a.opened_at || a.updated_at || 0);
  })
);

const getComplaintLineClass = (complaint) => {
  const status = String(complaint?.status || '').toUpperCase();
  if (status === 'OPEN') return 'line-open';
  if (isTerminalComplaintStatus(status)) return 'line-closed';
  if (status === 'DEFERRED') {
    const dueDate = complaint?.due_at ? new Date(complaint.due_at) : null;
    if (!dueDate || Number.isNaN(dueDate.getTime())) return 'line-deferred-safe';
    const hoursRemaining = (dueDate.getTime() - Date.now()) / 3600000;
    if (hoursRemaining < 0) return 'line-deferred-overdue';
    if (hoursRemaining < 24) return 'line-deferred-warning';
    return 'line-deferred-safe';
  }
  return 'line-open';
};

const DeferredComplaintCard = ({ title, complaint, onClick }) => {
  const status = String(complaint.status || 'DEFERRED').toUpperCase();
  const isOverdue = shouldShowOverdueStatus(complaint);
  const stateClass = isTerminalComplaintStatus(status) ? 'is-closed' : (isOverdue ? 'is-overdue' : 'is-deferred');
  const badgeLabel = isOverdue ? 'OVERDUE' : (complaint.status || 'DEFERRED');
  const badgeClass = isOverdue ? 'status-overdue' : getStatusClass(complaint.status);
  return (
    <div className="briefing-complaint-wrap" key={complaint.id}>
      {title ? <div className="briefing-list-title">{title}</div> : null}
      <button
        type="button"
        className={`briefing-complaint-card briefing-complaint-card-button ${stateClass} ${getComplaintLineClass(complaint)}`}
        onClick={onClick}
      >
        <div className="briefing-complaint-main">
          <div className="briefing-complaint-top">
            <span className="briefing-complaint-wo">{complaint.work_order_number || `WO.${complaint.id}`}</span>
            <span className={`briefing-complaint-badge ${badgeClass}`}>{badgeLabel}</span>
            <span className="briefing-complaint-tag">{complaint.category === 'Pilot' ? 'Pilot' : 'Maintenance'}</span>
          </div>
          <div className="briefing-complaint-desc">{complaint.defect_description}</div>
          <div className="briefing-complaint-meta">
            <div className="briefing-meta-item">
              <span className="briefing-meta-label">Opened</span>
              <span className="briefing-meta-value">{formatDisplayDate(complaint.opened_at)}</span>
            </div>
            <div className="briefing-meta-item">
              <span className="briefing-meta-label">Due at</span>
              <span className="briefing-meta-value">
                {complaint.due_at ? `${formatDisplayDate(complaint.due_at)}${complaint.due_hours ? ` (${complaint.due_hours} H)` : ''}` : '---'}
              </span>
            </div>
          </div>
        </div>
        <Chevron />
      </button>
    </div>
  );
};

const CrsRow = ({ lastCRS }) => (
  <div className="briefing-inline-row">
    <div className="briefing-inline-left">
      <img src={mccIcon} alt="" className="asr-inline-icon app-icon app-icon-mcc" />
      <span>Last Crs for {lastCRS?.work_order_number || 'WO.0000000'}</span>
    </div>
    <div className="briefing-inline-right">
      <span>approved LT.{lastCRS?.id || '---'} at {lastCRS?.location || '---'} on {formatDisplayDateTime(lastCRS?.performed_at)} UTC</span>
      <Chevron />
    </div>
  </div>
);

const RowButton = ({ children, className = '', onClick }) => (
  <button type="button" className={`briefing-inline-row briefing-inline-row-button ${className}`.trim()} onClick={onClick}>
    {children}
  </button>
);

const ClosedItemsRow = ({ count, onClick }) => (
  <RowButton onClick={onClick}>
    <div className="briefing-inline-left">
      <img src={openComplaintsIcon} alt="" className="asr-inline-icon app-icon app-icon-opencomplaints" />
      <span>Closed Items</span>
    </div>
    <div className="briefing-inline-right">
      <span className="briefing-muted-inline">Recently Closed</span>
      <span className="briefing-strong-inline">{count || 0}</span>
      <Chevron />
    </div>
  </RowButton>
);

const CabinItemsRow = ({ counts, onClick }) => (
  <RowButton className="briefing-inline-row-stats" onClick={onClick}>
    <div className="briefing-inline-left">
      <img src={cabinItemsIcon} alt="" className="asr-inline-icon app-icon app-icon-cabinitems" />
      <span>Cabin Items</span>
    </div>
    <div className="briefing-inline-stats">
      <div className="briefing-stat-col"><span>Open</span><strong>{counts.open}</strong></div>
      <div className="briefing-stat-col"><span>Accepted</span><strong>{counts.accepted}</strong></div>
      <div className="briefing-stat-col"><span>Deferred</span><strong>{counts.deferred}</strong></div>
      <div className="briefing-stat-col"><span>Recently Closed</span><strong>{counts.closed}</strong></div>
      <Chevron />
    </div>
  </RowButton>
);

const getDateForListType = (complaint, listType) => {
  if (listType === 'closed') return complaint.closed_at || complaint.updated_at || complaint.opened_at || null;
  return complaint.opened_at || complaint.updated_at || complaint.closed_at || null;
};

const getFilterValue = (complaint, fieldId) => {
  switch (fieldId) {
    case 'work_order_number':
      return `${complaint.work_order_number || ''} ${complaint.id || ''}`;
    case 'section':
      return complaint.section || '';
    case 'status':
      return complaint.status || '';
    case 'mcc_reference':
      return complaint.mcc_reference || '';
    case 'ata_chapter':
      return complaint.ata_chapter || '';
    case 'type':
      return `${complaint.type || ''} ${complaint.category || ''}`;
    default:
      return '';
  }
};

const CalendarField = ({
  fieldKey,
  label,
  value,
  isOpen,
  visibleMonth,
  onToggle,
  onChangeMonth,
  onSelectDate,
  onClear
}) => {
  const selectedDate = value ? parseISO(value) : null;
  const monthStart = startOfMonth(visibleMonth);
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 })
  });

  return (
    <div className={`briefing-calendar-field ${fieldKey === 'to' ? 'align-right' : 'align-left'} ${isOpen ? 'open' : ''}`}>
      <button
        type="button"
        className="briefing-browser-date-trigger"
        onClick={() => onToggle(fieldKey)}
      >
        <span className={`briefing-browser-date-value ${value ? 'has-value' : ''}`}>
          {value ? format(parseISO(value), 'dd.MMM.yyyy') : label}
        </span>
        <span className={`briefing-browser-filter-chevron ${isOpen ? 'open' : ''}`}>▾</span>
      </button>

      <div className={`briefing-calendar-popover ${isOpen ? 'open' : ''}`}>
        <div className="briefing-calendar-head">
          <button type="button" className="briefing-calendar-nav" onClick={() => onChangeMonth(fieldKey, -1)}>‹</button>
          <span className="briefing-calendar-title">{format(visibleMonth, 'MMMM yyyy')}</span>
          <button type="button" className="briefing-calendar-nav" onClick={() => onChangeMonth(fieldKey, 1)}>›</button>
        </div>

        <div className="briefing-calendar-weekdays">
          {CALENDAR_WEEKDAYS.map(day => <span key={day}>{day}</span>)}
        </div>

        <div className="briefing-calendar-grid">
          {calendarDays.map(day => (
            <button
              key={day.toISOString()}
              type="button"
              className={[
                'briefing-calendar-day',
                isSameMonth(day, visibleMonth) ? 'current-month' : 'outside-month',
                selectedDate && isSameDay(day, selectedDate) ? 'selected' : ''
              ].join(' ')}
              onClick={() => onSelectDate(fieldKey, day)}
            >
              {format(day, 'd')}
            </button>
          ))}
        </div>

        <div className="briefing-calendar-footer">
          <button type="button" className="briefing-calendar-clear" onClick={() => onClear(fieldKey)}>Clear</button>
        </div>
      </div>
    </div>
  );
};

const AircraftStatusReport = ({
  aircraftId,
  onFlyAircraft,
  showFlyButton = true,
  flyDisabled = false,
  flyDisabledReason = '',
  flyDisabledUserName = '',
  embeddedMode = false,
  onBack,
  backLabel = 'BRIEFING',
  initialSection = 'main',
  rootTitle,
  initialComplaintId = null,
  initialComplaint = null,
  initialDetailReturnView = 'main',
  detailBackLabel,
  refreshSignal = 0
}) => {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [report, setReport] = useState(null);
  const [allComplaints, setAllComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('main');
  const [listType, setListType] = useState('closed');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintActions, setComplaintActions] = useState([]);
  const [detailReturnView, setDetailReturnView] = useState('main');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterField, setFilterField] = useState('work_order_number');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openCalendar, setOpenCalendar] = useState(null);
  const [calendarMonths, setCalendarMonths] = useState({
    from: new Date(),
    to: new Date()
  });
  const [voidComplaintModalOpen, setVoidComplaintModalOpen] = useState(false);
  const [closeWorkorderModalOpen, setCloseWorkorderModalOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [toGoNow, setToGoNow] = useState(Date.now());
  const filterDropdownRef = useRef(null);
  const calendarWrapRef = useRef(null);
  const initialComplaintOpenedRef = useRef(null);
  const actionPendingRef = useRef(false);
  const containerRef = useRef(null);
  const reportRef = useRef(null);
  const viewRef = useRef('main');
  const scrollPositionsRef = useRef({});

  const changeView = useCallback((nextView) => {
    const currentContainer = containerRef.current;
    if (currentContainer) {
      scrollPositionsRef.current[viewRef.current] = currentContainer.scrollTop;
    }
    viewRef.current = nextView;
    setView(nextView);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => setToGoNow(Date.now()), 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (initialComplaintId) return;

    if (initialSection === 'closed') {
      setListType('closed');
      changeView('list');
      setSelectedComplaint(null);
      setComplaintActions([]);
      setFilterDropdownOpen(false);
      setOpenCalendar(null);
      return;
    }

    if (initialSection === 'cabin') {
      setListType('cabin');
      changeView('list');
      setSelectedComplaint(null);
      setComplaintActions([]);
      setFilterDropdownOpen(false);
      setOpenCalendar(null);
      return;
    }

    changeView('main');
    setSelectedComplaint(null);
    setComplaintActions([]);
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
  }, [changeView, initialComplaintId, initialSection]);

  useEffect(() => {
    if (!aircraftId) return;
    const isInitialLoad = String(reportRef.current?.aircraft?.id || '') !== String(aircraftId);
    if (isInitialLoad) setLoading(true);
    Promise.all([
      getAircraftStatusReport(aircraftId),
      getComplaints(aircraftId)
    ])
      .then(([statusRes, complaintsRes]) => {
        reportRef.current = statusRes.data.data;
        setReport(statusRes.data.data);
        setAllComplaints(complaintsRes.data.complaints || []);
      })
      .catch(console.error)
      .finally(() => {
        if (isInitialLoad) setLoading(false);
      });
  }, [aircraftId, refreshSignal]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const targetTop = scrollPositionsRef.current[view] || 0;
    container.scrollTop = targetTop;
    const rememberScroll = () => {
      scrollPositionsRef.current[view] = container.scrollTop;
    };
    container.addEventListener('scroll', rememberScroll, { passive: true });
    return () => {
      rememberScroll();
      container.removeEventListener('scroll', rememberScroll);
    };
  }, [loading, view]);

  const reloadAircraftData = async () => {
    if (!aircraftId) return;
    const [statusRes, complaintsRes] = await Promise.all([
      getAircraftStatusReport(aircraftId),
      getComplaints(aircraftId)
    ]);
    setReport(statusRes.data.data);
    setAllComplaints(complaintsRes.data.complaints || []);
  };

  useEffect(() => {
    const onMouseDown = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (calendarWrapRef.current && !calendarWrapRef.current.contains(event.target)) {
        setOpenCalendar(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const closedItems = useMemo(() => (
    allComplaints
      .filter(item => String(item.status || '').toUpperCase() === 'CLOSED')
      .sort((a, b) => new Date(getDateForListType(b, 'closed')) - new Date(getDateForListType(a, 'closed')))
  ), [allComplaints]);

  const cabinItems = useMemo(() => (
    allComplaints
      .filter(item => item.type === 'Cabin' || item.category === 'Cabin')
      .sort((a, b) => new Date(getDateForListType(b, 'cabin')) - new Date(getDateForListType(a, 'cabin')))
  ), [allComplaints]);

  const recentClosedCount = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return closedItems.filter(item => {
      const date = new Date(item.closed_at || item.updated_at || item.opened_at || 0);
      return !Number.isNaN(date.getTime()) && date >= cutoff;
    }).length;
  }, [closedItems]);

  const cabinCounts = useMemo(() => ({
    open: cabinItems.filter(item => String(item.status || '').toUpperCase() === 'OPEN').length,
    accepted: cabinItems.filter(item => String(item.status || '').toUpperCase() === 'ACCEPTED').length,
    deferred: cabinItems.filter(item => String(item.status || '').toUpperCase() === 'DEFERRED').length,
    closed: cabinItems.filter(item => String(item.status || '').toUpperCase() === 'CLOSED').length
  }), [cabinItems]);

  const briefingOpenItems = useMemo(() => (
    sortNextDueMaintenanceItems(
      uniqueComplaints(
        report?.openItems || [],
        allComplaints.filter(isNextDueMaintenanceItem)
      )
    )
  ), [allComplaints, report?.openItems]);

  const briefingMelComplaints = useMemo(() => (
    sortComplaintsNewestFirst(
      uniqueComplaints(
        (report?.airworthyDeferred || []).filter(isMelComplaint),
        allComplaints.filter(isMelComplaint)
      )
    )
  ), [allComplaints, report?.airworthyDeferred]);

  const briefingDdHilComplaints = useMemo(() => (
    sortComplaintsNewestFirst(
      uniqueComplaints(
        (report?.nonAirworthyDeferred || []).filter(isDdHilComplaint),
        allComplaints.filter(isDdHilComplaint)
      )
    )
  ), [allComplaints, report?.nonAirworthyDeferred]);

  const activeItems = listType === 'closed' ? closedItems : cabinItems;

  const filterOptionCounts = useMemo(() => (
    FILTER_OPTIONS.reduce((acc, option) => {
      acc[option.id] = activeItems.filter(item => String(getFilterValue(item, option.id)).trim()).length;
      return acc;
    }, {})
  ), [activeItems]);

  const filteredItems = useMemo(() => {
    return activeItems.filter(item => {
      const filterValue = String(getFilterValue(item, filterField)).toLowerCase();
      const matchesSearch = !searchTerm.trim() || filterValue.includes(searchTerm.trim().toLowerCase());

      const listDateValue = getDateForListType(item, listType);
      const listDate = listDateValue ? new Date(listDateValue) : null;
      const listDateKey = listDate && !Number.isNaN(listDate.getTime()) ? format(listDate, 'yyyy-MM-dd') : '';
      const matchesFrom = !dateFrom || (listDateKey && listDateKey >= dateFrom);
      const matchesTo = !dateTo || (listDateKey && listDateKey <= dateTo);

      return matchesSearch && matchesFrom && matchesTo;
    });
  }, [activeItems, dateFrom, dateTo, filterField, listType, searchTerm]);

  const openListView = (nextListType) => {
    setListType(nextListType);
    setSelectedComplaint(null);
    setComplaintActions([]);
    changeView('list');
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
  };

  const openDetailView = useCallback(async (complaint, returnView = view) => {
    setSelectedComplaint(complaint);
    setComplaintActions([]);
    setDetailReturnView(returnView);
    changeView('detail');
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
    if (!complaint?.id) return;
    const requests = [getComplaint(complaint.id)];
    if (complaintActionsEnabled) requests.push(getComplaintActions(complaint.id));
    const [complaintResult, actionsResult] = await Promise.allSettled(requests);
    if (complaintResult.status === 'fulfilled') {
      const complaintRes = complaintResult.value;
      setSelectedComplaint(complaintRes.data.complaint || complaint);
    } else {
      console.error(complaintResult.reason);
    }
    if (actionsResult?.status === 'fulfilled') {
      setComplaintActions(actionsResult.value.data.actions || []);
    } else if (actionsResult && actionsResult.reason?.response?.status !== 404) {
      console.error(actionsResult.reason);
    }
  }, [changeView, view]);

  useEffect(() => {
    if (!initialComplaintId) {
      initialComplaintOpenedRef.current = null;
      return;
    }

    const complaintKey = String(initialComplaintId);
    if (initialComplaintOpenedRef.current === complaintKey) return;
    initialComplaintOpenedRef.current = complaintKey;

    const seededComplaint = initialComplaint
      || allComplaints.find(item => String(item.id) === complaintKey)
      || { id: initialComplaintId };

    openDetailView(seededComplaint, initialDetailReturnView);
  }, [allComplaints, initialComplaint, initialComplaintId, initialDetailReturnView, openDetailView]);

  const reloadDetailView = async () => {
    if (!selectedComplaint?.id) return;
    const requests = [getComplaint(selectedComplaint.id)];
    if (complaintActionsEnabled) requests.push(getComplaintActions(selectedComplaint.id));
    const [complaintResult, actionsResult] = await Promise.allSettled(requests);
    if (complaintResult.status === 'fulfilled') {
      const complaintRes = complaintResult.value;
      setSelectedComplaint(complaintRes.data.complaint || selectedComplaint);
    } else {
      console.error(complaintResult.reason);
    }
    if (actionsResult?.status === 'fulfilled') {
      setComplaintActions(actionsResult.value.data.actions || []);
    } else if (actionsResult && actionsResult.reason?.response?.status !== 404) {
      console.error(actionsResult.reason);
    }
  };

  useEffect(() => {
    if (!selectedComplaint?.id || !refreshSignal) return;
    reloadDetailView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const beginComplaintAction = () => {
    if (actionPendingRef.current) return false;
    actionPendingRef.current = true;
    setActionPending(true);
    return true;
  };

  const endComplaintAction = () => {
    actionPendingRef.current = false;
    setActionPending(false);
  };

  const handleToggleComplaintLock = async () => {
    if (!selectedComplaint?.id || !isAdmin) return;
    if (!beginComplaintAction()) return;
    try {
      const nextLocked = !isComplaintLocked(selectedComplaint);
      await updateComplaint(selectedComplaint.id, {
        is_locked: nextLocked,
        action_type: 'LOCK'
      });
      await Promise.all([reloadDetailView(), reloadAircraftData()]);
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const handleCloseWorkorder = async () => {
    if (!selectedComplaint?.id) return;
    if (isComplaintLocked(selectedComplaint) && !isAdmin) {
      setCloseWorkorderModalOpen(false);
      return;
    }
    if (!beginComplaintAction()) return;
    setCloseWorkorderModalOpen(false);
    try {
      await updateComplaint(selectedComplaint.id, {
        status: 'CLOSED',
        action_type: 'STATUS',
        action_text: 'WORKORDER CLOSED.'
      });
      await Promise.all([reloadDetailView(), reloadAircraftData()]);
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const handleVoidWorkorder = async () => {
    if (!selectedComplaint?.id) return;
    if (isComplaintLocked(selectedComplaint) && !isAdmin) {
      setVoidComplaintModalOpen(false);
      return;
    }
    if (!beginComplaintAction()) return;
    setVoidComplaintModalOpen(false);
    try {
      const actionText = VOID_ACTION_TEXT;
      await updateComplaint(selectedComplaint.id, {
        status: 'CLOSED',
        section: 'voided',
        mcc_reference: actionText,
        action_type: 'VOID',
        action_text: actionText
      });
      await Promise.all([reloadDetailView(), reloadAircraftData()]);
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const renderVoidComplaintModal = () => createPortal((
    <div className="void-complaint-overlay" role="presentation" onMouseDown={() => !actionPending && setVoidComplaintModalOpen(false)}>
      <div className="void-complaint-modal" role="dialog" aria-modal="true" onMouseDown={event => event.stopPropagation()}>
        <div className="void-complaint-header">
          <span>Confirm Void Complaint</span>
        </div>
        <div className="void-complaint-body">
          You are about to void the complaint.
        </div>
        <div className="void-complaint-footer">
          <button type="button" className="void-complaint-btn" onClick={() => setVoidComplaintModalOpen(false)} disabled={actionPending}>
            <span aria-hidden="true">⊘</span>
            <span>Cancel</span>
          </button>
          <button type="button" className="void-complaint-btn void-complaint-confirm" onClick={handleVoidWorkorder} disabled={actionPending || !selectedComplaint?.id}>
            <span aria-hidden="true">✓</span>
            <span>{actionPending ? 'Saving...' : 'Confirm'}</span>
          </button>
        </div>
      </div>
    </div>
  ), document.body);

  const renderCloseWorkorderModal = () => createPortal((
    <div className="void-complaint-overlay" role="presentation" onMouseDown={() => !actionPending && setCloseWorkorderModalOpen(false)}>
      <div className="void-complaint-modal" role="dialog" aria-modal="true" onMouseDown={event => event.stopPropagation()}>
        <div className="void-complaint-header">
          <span>Confirm Close Workorder</span>
        </div>
        <div className="void-complaint-body">
          You are about to close the workorder.
        </div>
        <div className="void-complaint-footer">
          <button type="button" className="void-complaint-btn" onClick={() => setCloseWorkorderModalOpen(false)} disabled={actionPending}>
            <span aria-hidden="true">⊘</span>
            <span>Cancel</span>
          </button>
          <button type="button" className="void-complaint-btn void-complaint-confirm" onClick={handleCloseWorkorder} disabled={actionPending || !selectedComplaint?.id}>
            <span aria-hidden="true">✓</span>
            <span>{actionPending ? 'Saving...' : 'Confirm'}</span>
          </button>
        </div>
      </div>
    </div>
  ), document.body);

  const handleBack = () => {
    if (view === 'detail') {
      if (detailReturnView === 'external' && embeddedMode && onBack) {
        onBack();
        return;
      }
      changeView(detailReturnView || 'main');
      return;
    }
    if (embeddedMode && onBack) {
      onBack();
      return;
    }
    changeView('main');
    setSelectedComplaint(null);
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
  };

  const handleCalendarToggle = (fieldKey) => {
    setFilterDropdownOpen(false);
    setOpenCalendar(current => current === fieldKey ? null : fieldKey);
    setCalendarMonths(prev => ({
      ...prev,
      [fieldKey]: fieldKey === 'from' && dateFrom ? parseISO(dateFrom) : fieldKey === 'to' && dateTo ? parseISO(dateTo) : prev[fieldKey]
    }));
  };

  const handleCalendarMonthChange = (fieldKey, direction) => {
    setCalendarMonths(prev => ({
      ...prev,
      [fieldKey]: direction > 0 ? addMonths(prev[fieldKey], 1) : subMonths(prev[fieldKey], 1)
    }));
  };

  const handleCalendarSelect = (fieldKey, day) => {
    const nextValue = format(day, 'yyyy-MM-dd');
    if (fieldKey === 'from') {
      setDateFrom(nextValue);
      if (dateTo && dateTo < nextValue) setDateTo(nextValue);
    } else {
      setDateTo(nextValue);
      if (dateFrom && dateFrom > nextValue) setDateFrom(nextValue);
    }
    setOpenCalendar(null);
  };

  const handleCalendarClear = (fieldKey) => {
    if (fieldKey === 'from') setDateFrom('');
    if (fieldKey === 'to') setDateTo('');
    setOpenCalendar(null);
  };

  if (loading || !report || String(report.aircraft?.id || '') !== String(aircraftId)) {
    return (
      <div className="asr-container">
        <div className="spinner" style={{ margin: 'auto' }} />
      </div>
    );
  }

  const selectedFilter = FILTER_OPTIONS.find(option => option.id === filterField);
  const listTitle = listType === 'closed' ? 'Closed Items' : 'Cabin Items';
  const headerTitle = view === 'detail'
    ? 'Workorder Detail'
    : (view === 'main' ? (rootTitle || 'Briefing') : (rootTitle || listTitle));
  const headerBackLabel = view === 'detail' ? (detailBackLabel || 'OVERVIEW') : backLabel;
  const latestDispatchAction = [...complaintActions].reverse().find(action => {
    if (action.action_type === 'DISPATCH') return true;
    const metadata = parseActionMetadata(action.metadata_json);
    return Boolean(metadata.category || metadata.deferral_type);
  });
  const latestDispatchMeta = parseActionMetadata(latestDispatchAction?.metadata_json);
  const cachedDeferral = getCachedDeferralData(selectedComplaint?.id);
  const displayText = (...values) => values.find(value => {
    const text = String(value ?? '').trim();
    return text && text !== '---';
  }) || 'N/A';
  const mergedDeferralComplaint = { ...cachedDeferral, ...selectedComplaint };
  const mergedDispatchMeta = { ...cachedDeferral, ...latestDispatchMeta };
  const workorderIssueStation = displayText(mergedDeferralComplaint?.issue_station, mergedDeferralComplaint?.deferral_station, mergedDispatchMeta.deferral_station);
  const storedDueHours = Number(mergedDeferralComplaint?.due_hours);
  const storedDueDays = Number.isFinite(storedDueHours) && storedDueHours > 0 ? `${Math.round(storedDueHours / 24)} Days` : '';
  const liveDueDays = formatRemainingToGoDays(selectedComplaint?.due_at, toGoNow);
  const workorderToGo = displayText(
    liveDueDays,
    mergedDispatchMeta.to_go_days ? `${mergedDispatchMeta.to_go_days} Days` : '',
    mergedDispatchMeta.to_go_fh ? `${mergedDispatchMeta.to_go_fh} FH` : '',
    mergedDispatchMeta.to_go_fc ? `${mergedDispatchMeta.to_go_fc} FC` : '',
    storedDueDays,
    formatPreciseToGo(selectedComplaint?.due_at)
  );
  const workorderDefValue = displayText(
    mergedDispatchMeta.to_go_days ? `${mergedDispatchMeta.to_go_days} Days` : '',
    storedDueDays,
    formatPreciseToGo(selectedComplaint?.due_at)
  );
  const workorderMelRef = displayText(mergedDeferralComplaint?.mel_cdl_ref, extractMelReference(mergedDispatchMeta.mel_reference, mergedDispatchMeta.document_reference, selectedComplaint?.mcc_reference));
  const workorderDefType = displayText(formatDeferralType(mergedDispatchMeta, mergedDeferralComplaint));
  const workorderDefStation = displayText(mergedDeferralComplaint?.deferral_station, mergedDispatchMeta.deferral_station, mergedDeferralComplaint?.issue_station);
  const workorderDefReason = displayText(mergedDeferralComplaint?.deferral_reason, mergedDispatchMeta.deferral_reason);
  const workorderDefDate = displayText(mergedDeferralComplaint?.deferral_date, mergedDispatchMeta.deferral_date, formatDisplayDateTime(latestDispatchAction?.performed_at), formatDisplayDateTime(selectedComplaint?.updated_at));
  const workorderDefCreation = displayText(mergedDeferralComplaint?.deferral_creation, formatDisplayDateTime(latestDispatchAction?.performed_at), formatDisplayDateTime(selectedComplaint?.updated_at));
  const selectedStatus = String(selectedComplaint?.status || '').toUpperCase();
  const complaintLocked = isComplaintLocked(selectedComplaint);
  const canEditComplaint = !complaintLocked || isAdmin;
  const workorderCardStateClass = isTerminalComplaintStatus(selectedStatus)
    ? 'is-closed'
    : (isComplaintOverdue(selectedComplaint) ? 'is-overdue' : selectedStatus === 'OPEN' ? 'is-open' : 'is-deferred');
  const hasVoidAction = complaintActions.some(action => action.action_type === 'VOID' || String(action.action_text || '').toLowerCase().includes('void'));
  const showFallbackVoidAction = selectedComplaint?.section === 'voided' && !hasVoidAction;
  const renderWorkorderActionCard = (action, actionNumber) => {
    const metadata = parseActionMetadata(action.metadata_json);
    const isVoidAction = action.action_type === 'VOID' || String(action.action_text || '').toLowerCase().includes('void');
    const isActionEntry = action.action_type === 'ACTION';
    const intervalParts = [
      metadata.inspection_interval_days ? `${metadata.inspection_interval_days} DAY(S)` : '',
      metadata.inspection_interval_fh ? `${metadata.inspection_interval_fh} FH` : '',
      metadata.inspection_interval_fc ? `${metadata.inspection_interval_fc} FC` : ''
    ].filter(Boolean);

    return (
      <div className={`workorder-history-card ${isVoidAction ? 'workorder-history-action-card' : ''}`} key={`action-${action.id || actionNumber}`}>
        <div className="workorder-history-head">
          <span>Action {actionNumber}</span>
          <span>{formatDisplayDateTime(action.performed_at)}</span>
        </div>
        <strong className={isActionEntry ? 'workorder-history-text-normal' : ''}>{isVoidAction ? (metadata.action || action.action_text || VOID_ACTION_TEXT) : (action.action_text || 'Complaint updated.')}</strong>
        {metadata.repetitive_inspection ? (
          <>
            <strong>REPETITIVE INSPECTION CREATED:</strong>
            <strong className="workorder-history-text-normal">{metadata.repetitive_inspection_description || 'DO INSPECTION'}</strong>
          </>
        ) : null}
        {intervalParts.length ? <strong>REPETITIVE INSPECTION INTERVALS: {intervalParts.join(' / ')}</strong> : null}
        {action.performed_by_name ? <strong>PERFORMED SIGN: {action.performed_by_name}</strong> : null}
      </div>
    );
  };
  const workorderHistoryCards = [];
  let actionCardNumber = 0;
  let hasOpenedStatusUpdate = false;

  complaintActions.forEach((action, index) => {
    const statusUpdateText = getWorkorderStatusUpdateText(action);
    if (statusUpdateText) {
      if (statusUpdateText === 'WORKORDER OPENED.') hasOpenedStatusUpdate = true;
      workorderHistoryCards.push(
        <div className="workorder-history-card" key={`status-${action.id || index}`}>
          <div className="workorder-history-head">
            <span>Status Update</span>
            <span>{formatDisplayDateTime(action.performed_at)}</span>
          </div>
          <strong>{statusUpdateText}</strong>
        </div>
      );
    }

    if (shouldRenderWorkorderActionCard(action)) {
      actionCardNumber += 1;
      workorderHistoryCards.push(renderWorkorderActionCard(action, actionCardNumber));
    }
  });

  if (selectedComplaint && !hasOpenedStatusUpdate) {
    workorderHistoryCards.unshift(
      <div className="workorder-history-card" key="status-opened-fallback">
        <div className="workorder-history-head">
          <span>Status Update</span>
          <span>{formatDisplayDateTime(selectedComplaint?.opened_at)}</span>
        </div>
        <strong>WORKORDER OPENED.</strong>
      </div>
    );
  }

  if (showFallbackVoidAction) {
    actionCardNumber += 1;
    workorderHistoryCards.push(
      <div className="workorder-history-card workorder-history-action-card" key="void-fallback">
        <div className="workorder-history-head">
          <span>Action {actionCardNumber}</span>
          <span>{formatDisplayDateTime(selectedComplaint?.closed_at || selectedComplaint?.updated_at)}</span>
        </div>
        <strong>{selectedComplaint?.mcc_reference || VOID_ACTION_TEXT}</strong>
      </div>
    );
  }

  return (
    <div className="asr-container" ref={containerRef}>
      <div className="asr-header-shell">
        {view === 'main' && !embeddedMode ? (
          <>
            <div className="asr-page-header">
              <div className="asr-title-pill">Briefing</div>
              {showFlyButton ? (
                <div className="asr-fly-shell">
                  {flyDisabled ? (
                    <div className="asr-fly-status-message" title={flyDisabledReason}>
                      <span>Aircraft is currently flying</span>
                      <span>{flyDisabledUserName || 'Unknown user'}</span>
                    </div>
                  ) : null}
                  <button
                    className={`asr-fly-btn ${flyDisabled ? 'disabled' : ''}`}
                    type="button"
                    onClick={onFlyAircraft}
                    disabled={flyDisabled}
                    title={flyDisabledReason}
                  >
                    <span className="asr-fly-btn-text">Fly Aircraft</span>
                  </button>
                </div>
              ) : <div className="asr-header-spacer" />}
            </div>
            <div className="asr-header-lower" />
          </>
        ) : (
          <>
            <div className="asr-page-header asr-page-header-subview">
              <button type="button" className="asr-back-btn" onClick={handleBack}>
                <img src={arrowLeftIcon} alt="" className="asr-back-arrow app-icon app-icon-arrow-sm-left" />
                {headerBackLabel}
              </button>
              <div className="asr-page-title">{headerTitle}</div>
              <div className="asr-header-spacer" />
            </div>
            <div className="asr-header-lower" />
          </>
        )}
      </div>

      <div className="asr-content">
        {view === 'main' ? (
          <>
            <SectionTitle>Aircraft</SectionTitle>
            <div className="asr-info-card">
              <div className="asr-info-col">
                <span className="asr-info-label">AC Reg.:</span>
                <span className="asr-info-val">{report.aircraft.registration}</span>
              </div>
              <div className="asr-info-col">
                <span className="asr-info-label">AC Verification Code:</span>
                <span className="asr-info-val">{report.aircraft.ac_verification_code}</span>
              </div>
            </div>

            <SectionTitle>Next Due Maintenance Events</SectionTitle>
            <NextDueEventsCard items={briefingOpenItems} onOpenItem={item => openDetailView(item, 'main')} />

            <SectionTitle>Aircraft Downgrades/Limitations</SectionTitle>
            {report.downgrades?.length ? (
              report.downgrades.map(item => <DowngradeCard key={item.id} item={item} />)
            ) : (
              <CompactInfoRow icon={mccIcon} iconClassName="app-icon-mcc" label="Aircraft Downgrades/Limitations" />
            )}

            <SectionTitle>MEL Complaints</SectionTitle>
            {briefingMelComplaints.length ? (
              briefingMelComplaints.map(item => (
                <DeferredComplaintCard key={item.id} complaint={item} onClick={() => openDetailView(item, 'main')} />
              ))
            ) : (
              <EmptyStrip />
            )}

            <SectionTitle>DD HIL Complaints</SectionTitle>
            {briefingDdHilComplaints.length ? (
              briefingDdHilComplaints.map(item => (
                <DeferredComplaintCard key={item.id} complaint={item} onClick={() => openDetailView(item, 'main')} />
              ))
            ) : (
              <EmptyStrip />
            )}

            <SectionTitle>Technical Service Notices to Crew</SectionTitle>
            {report.techNotices?.length ? (
              report.techNotices.map(item => (
                <CompactInfoRow
                  key={item.id}
                  icon={mccIcon}
                  iconClassName="app-icon-mcc"
                  label={item.work_order_number || item.defect_description || 'Technical Service Notice'}
                  rightText={item.status || 'open'}
                />
              ))
            ) : (
              <CompactInfoRow icon={mccIcon} iconClassName="app-icon-mcc" label="Technical Service Notices to Crew" />
            )}

            <SectionTitle>Last Release to Service (CRS)</SectionTitle>
            {report.lastCRS ? (
              <CrsRow lastCRS={report.lastCRS} />
            ) : (
              <CompactInfoRow icon={mccIcon} iconClassName="app-icon-mcc" label="Last Crs" />
            )}

            <SectionTitle>Other Information</SectionTitle>
            <ClosedItemsRow count={recentClosedCount} onClick={() => openListView('closed')} />
            <CabinItemsRow counts={cabinCounts} onClick={() => openListView('cabin')} />
          </>
        ) : view === 'list' ? (
          <>
            <div className="briefing-browser-toolbar">
              <div className="briefing-browser-filter-cluster">
                <span className="briefing-browser-label">Filter</span>
                <div className="briefing-browser-filter-dropdown" ref={filterDropdownRef}>
                  <button
                    type="button"
                    className="briefing-browser-filter-trigger"
                    onClick={() => setFilterDropdownOpen(open => !open)}
                  >
                    <span>{selectedFilter?.label || 'Filter'}</span>
                    <span className={`briefing-browser-filter-chevron ${filterDropdownOpen ? 'open' : ''}`}>▾</span>
                  </button>
                  <div className={`briefing-browser-filter-menu ${filterDropdownOpen ? 'open' : ''}`}>
                    {FILTER_OPTIONS.map(option => (
                      <button
                        key={option.id}
                        type="button"
                        className={`briefing-browser-filter-option ${filterField === option.id ? 'active' : ''}`}
                        onClick={() => {
                          setFilterField(option.id);
                          setFilterDropdownOpen(false);
                        }}
                      >
                        <span>{option.label}</span>
                        <span>{filterOptionCounts[option.id] || 0}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  className="briefing-browser-search"
                  placeholder="Search for..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="briefing-browser-date-cluster" ref={calendarWrapRef}>
                <span className="briefing-browser-label">Date</span>
                <CalendarField
                  fieldKey="from"
                  label="From"
                  value={dateFrom}
                  isOpen={openCalendar === 'from'}
                  visibleMonth={calendarMonths.from}
                  onToggle={handleCalendarToggle}
                  onChangeMonth={handleCalendarMonthChange}
                  onSelectDate={handleCalendarSelect}
                  onClear={handleCalendarClear}
                />
                <span className="briefing-browser-date-sep">-</span>
                <CalendarField
                  fieldKey="to"
                  label="To"
                  value={dateTo}
                  isOpen={openCalendar === 'to'}
                  visibleMonth={calendarMonths.to}
                  onToggle={handleCalendarToggle}
                  onChangeMonth={handleCalendarMonthChange}
                  onSelectDate={handleCalendarSelect}
                  onClear={handleCalendarClear}
                />
              </div>
            </div>

            <div className="briefing-browser-results">
              {filteredItems.length ? filteredItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={`briefing-browser-item ${getStatusClass(item.status)} ${getComplaintLineClass(item)}`}
                  onClick={() => openDetailView(item)}
                >
                  <div className="briefing-browser-item-main">
                    <div className="briefing-browser-item-top">
                      <span className="briefing-browser-item-wo">{item.work_order_number || `WO.${item.id}`}</span>
                      <span className={`briefing-browser-item-badge ${getStatusBadgeClass(item)}`}>{getStatusBadgeLabel(item)}</span>
                      <span className="briefing-browser-item-tag">{item.type || item.category || 'Item'}</span>
                    </div>
                    <div className="briefing-browser-item-desc">{item.defect_description || 'No description provided'}</div>
                    <div className="briefing-browser-item-meta">
                      <div className="briefing-browser-meta-field">
                        <span>Opened</span>
                        <strong>{formatDisplayDate(item.opened_at)}</strong>
                      </div>
                      <div className="briefing-browser-meta-field">
                        <span>{listType === 'closed' ? 'Closed' : 'Current Date'}</span>
                        <strong>{formatDisplayDate(getDateForListType(item, listType))}</strong>
                      </div>
                      <div className="briefing-browser-meta-field">
                        <span>ATA</span>
                        <strong>{item.ata_chapter || '---'}</strong>
                      </div>
                    </div>
                  </div>
                  <Chevron />
                </button>
              )) : (
                <EmptyStrip label="no matching items" />
              )}
            </div>
          </>
        ) : (
          <div className="workorder-detail-page">
            <div className={`workorder-detail-card ${workorderCardStateClass} ${getComplaintLineClass(selectedComplaint)}`}>
              <div className="workorder-detail-main">
                <div className="workorder-detail-top">
                  <span className="workorder-detail-wo">{selectedComplaint?.work_order_number || `WO.${selectedComplaint?.id || '---'}`}</span>
                  <div className="workorder-detail-badges">
                    <span className={`workorder-status-badge ${getStatusBadgeClass(selectedComplaint)}`}>{getStatusBadgeLabel(selectedComplaint)}</span>
                    <span className="workorder-type-badge">{selectedComplaint?.type || selectedComplaint?.category || 'Pilot'}</span>
                    {complaintLocked && isAdmin ? <span className="workorder-lock-badge">Locked</span> : null}
                  </div>
                  {isAdmin ? (
                    <button type="button" className={`workorder-action-btn workorder-lock-btn workorder-detail-lock-btn ${complaintLocked ? 'is-locked' : ''}`} onClick={handleToggleComplaintLock} disabled={actionPending}>
                      {complaintLocked ? 'Unlock Editing' : 'Lock Editing'}
                    </button>
                  ) : null}
                </div>

                <div className="workorder-detail-desc">{selectedComplaint?.defect_description || 'No description provided'}</div>

                <div className="workorder-detail-meta-grid">
                  <div className="workorder-meta-pair"><span>Opened</span><strong>{formatDisplayDateTime(selectedComplaint?.opened_at)}</strong></div>
                  <div className="workorder-meta-pair"><span>Due at</span><strong>{selectedComplaint?.due_at ? formatDisplayDate(selectedComplaint.due_at) : 'N/A'}</strong></div>
                  <div className="workorder-meta-pair"><span>Issue Station</span><strong>{workorderIssueStation}</strong></div>
                  <div className="workorder-meta-pair"><span>To Go</span><strong>{workorderToGo}</strong></div>
                  <div className="workorder-meta-pair"><span>ATA</span><strong>{selectedComplaint?.ata_chapter || 'N/A'}</strong></div>
                  <div className="workorder-meta-pair"><span>Def. Value</span><strong>{workorderDefValue}</strong></div>
                </div>

                <div className="workorder-deferral-lines">
                  <div className="workorder-deferral-line workorder-deferral-line-ref">
                    <div className="workorder-meta-pair"><span>MEL Ref</span><strong>{workorderMelRef}</strong></div>
                  </div>
                  <div className="workorder-deferral-line workorder-deferral-line-main">
                    <div className="workorder-meta-pair"><span>Deferral Type</span><strong>{workorderDefType}</strong></div>
                    <div className="workorder-meta-pair"><span>Def. Station</span><strong>{workorderDefStation}</strong></div>
                    <div className="workorder-meta-pair"><span>Def. Reason</span><strong>{workorderDefReason}</strong></div>
                    <div className="workorder-meta-pair"><span>Def. Date</span><strong>{workorderDefDate}</strong></div>
                  </div>
                  <div className="workorder-deferral-line workorder-deferral-line-creation">
                    <div className="workorder-meta-pair"><span>Def. Creation</span><strong>{workorderDefCreation}</strong></div>
                  </div>
                </div>
              </div>
            </div>

            {selectedComplaint && selectedComplaint.status !== 'CLOSED' && canEditComplaint ? (
              <div className="workorder-detail-actions">
                <button type="button" className="workorder-action-btn" onClick={() => setVoidComplaintModalOpen(true)} disabled={actionPending}>Void Complaint</button>
                <button type="button" className="workorder-action-btn" onClick={() => setCloseWorkorderModalOpen(true)} disabled={actionPending}>Close Workorder</button>
              </div>
            ) : null}

            <div className="workorder-history-list">
              {workorderHistoryCards}
            </div>
          </div>
        )}
      </div>
      {voidComplaintModalOpen ? renderVoidComplaintModal() : null}
      {closeWorkorderModalOpen ? renderCloseWorkorderModal() : null}
    </div>
  );
};

export default AircraftStatusReport;

